import { useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { ClipboardList, Send, ExternalLink, Copy, CheckCircle2, Clock, Search, UserPlus, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";
import { SendConsentDialog } from "@/components/HipaaDisclosure";

const Status = ({ ok, label, date }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] ${ok ? 'text-emerald-300' : 'text-amber-300'}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}{label}{ok && date ? ` · ${format(parseISO(date), 'MMM d')}` : ''}
  </span>
);

export const IntakeForms = ({ patients, appointments, onNewPatient, onOpenPatient }) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('pending');
  const [consentFor, setConsentFor] = useState(null);
  const [busy, setBusy] = useState(null);

  const rows = useMemo(() => {
    const now = Date.now();
    const next = {};
    appointments.forEach(a => {
      if (['cancelled', 'completed'].includes(a.status) || new Date(a.start_datetime) < now) return;
      if (!next[a.patient_id] || a.start_datetime < next[a.patient_id]) next[a.patient_id] = a.start_datetime;
    });
    const s = q.trim().toLowerCase();
    return patients
      .map(p => ({ ...p, next_visit: next[p.id], complete: !!p.intake_completed_at && !!p.treatment_consent && !!p.hipaa_acknowledged }))
      .filter(p => filter === 'all' || (filter === 'pending' ? !p.complete : p.complete))
      .filter(p => !s || `${p.first_name} ${p.last_name} ${p.email || ''} ${p.phone || ''}`.toLowerCase().includes(s))
      .sort((a, b) => (a.next_visit || '9').localeCompare(b.next_visit || '9') || (b.created_at || '').localeCompare(a.created_at || ''));
  }, [patients, appointments, q, filter]);

  const sendIntake = async (p) => {
    if (!p.email) { toast.error('Patient has no email on file'); return; }
    setBusy(p.id + 'send');
    try { await axios.post(`${API}/patients/${p.id}/send-intake`); toast.success(`Intake form emailed to ${p.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const openLink = async (p, which) => {
    setBusy(p.id + which);
    try {
      const r = await axios.get(`${API}/patients/${p.id}/intake-link`);
      const url = which === 'consent' ? r.data.consent_url : r.data.url;
      if (which === 'copy') { await navigator.clipboard.writeText(url); toast.success('Intake link copied'); }
      else window.open(url, '_blank', 'noopener');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const pending = patients.filter(p => !(p.intake_completed_at && p.treatment_consent && p.hipaa_acknowledged)).length;

  return (
    <div className="space-y-5" data-testid="intake-forms">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold brand-heading gold-gradient-text flex items-center gap-2"><ClipboardList className="w-6 h-6 text-primary" />New Patient Intake Form</h2>
          <p className="text-xs text-muted-foreground mt-1">Send the intake form + HIPAA/consent to a patient to complete in advance, or open it on this device to complete together during the first visit. Submissions save straight into the patient's chart.</p>
        </div>
        <Button onClick={onNewPatient} data-testid="intake-new-patient" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><UserPlus className="w-4 h-4 mr-1" />New patient</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search patient…" className="pl-9" data-testid="intake-search" />
        </div>
        {[['pending', `Needs intake (${pending})`], ['complete', 'Completed'], ['all', 'All']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`intake-filter-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${filter === k ? 'border-primary bg-primary/15 text-primary' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>{l}</button>
        ))}
      </div>

      <div className="rounded-xl border border-border/50 divide-y divide-border/30 overflow-hidden">
        {rows.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">No patients match.</p>}
        {rows.map(p => (
          <div key={p.id} className="p-4 flex flex-wrap items-center gap-3 hover:bg-primary/5" data-testid={`intake-row-${p.id}`}>
            <button type="button" onClick={() => onOpenPatient(p.id)} className="text-left min-w-[200px] flex-1" data-testid={`intake-open-${p.id}`}>
              <div className="font-semibold">{p.first_name} {p.last_name} {p.complete && <Badge className="ml-1 bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px]">Complete</Badge>}</div>
              <p className="text-xs text-muted-foreground">{p.email || 'no email'} · {p.phone || 'no phone'}{p.next_visit ? ` · next visit ${format(parseISO(p.next_visit), 'MMM d, h:mm a')}` : ''}</p>
              <div className="flex flex-wrap gap-3 mt-1">
                <Status ok={!!p.intake_completed_at} label={p.intake_completed_at ? 'Intake submitted' : 'Intake pending'} date={p.intake_completed_at} />
                <Status ok={!!p.hipaa_acknowledged && !!p.treatment_consent} label={p.hipaa_acknowledged && p.treatment_consent ? 'HIPAA + consent signed' : 'Consent unsigned'} date={p.consent_signed_at} />
              </div>
            </button>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" disabled={busy === p.id + 'send'} onClick={() => sendIntake(p)} className="border-primary/40 text-primary h-8 text-xs" data-testid={`intake-send-${p.id}`}><Send className="w-3.5 h-3.5 mr-1" />Email intake</Button>
              <Button size="sm" variant="outline" disabled={busy === p.id + 'open'} onClick={() => openLink(p, 'open')} className="h-8 text-xs" data-testid={`intake-complete-now-${p.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Complete now</Button>
              <Button size="sm" variant="ghost" disabled={busy === p.id + 'copy'} onClick={() => openLink(p, 'copy')} className="h-8 text-xs" data-testid={`intake-copy-${p.id}`}><Copy className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => setConsentFor(p.id)} className="h-8 text-xs border-amber-500/40 text-amber-300" data-testid={`intake-consent-${p.id}`}><FileSignature className="w-3.5 h-3.5 mr-1" />Send HIPAA/consent</Button>
            </div>
          </div>
        ))}
      </div>
      <SendConsentDialog open={!!consentFor} onOpenChange={(o) => !o && setConsentFor(null)} patients={patients} presetPatientId={consentFor} />
    </div>
  );
};
